import React, { useMemo } from "react";
import { Subject } from "rxjs";
import { scan, shareReplay } from "rxjs/operators";

import { TableProvider, TABLE_INIT_STATE } from "./TableContext";
import {
  IAction,
  TableActionTypes,
  TSortDirection,
  ITableContextValue,
  IColumn,
  TFilter
} from "./interfaces";
import { parseCell } from "./utils";
import { isEmpty } from "../../utils";

const RxTable: React.FC<{ children: React.ReactNode }> = props => {
  const tableContext = useMemo<ITableContextValue>(() => {
    const action$ = new Subject<IAction>();
    const dispatch = (action: IAction): void => {
      action$.next(action);
    };

    const tableState$ = action$.pipe(
      scan(
        (tableState, action) => {
          const { payload, name } = action;
          switch (action.type) {
            case TableActionTypes.init: {
              return {
                ...TABLE_INIT_STATE,
                rowsPerPage: payload.rowsPerPage,
                columns: payload.columns,
                rawData: payload.data,
                id: payload.id,
                filterData: payload.data,
                sorting: payload.sorting,
                loading: payload.loading
              };
            }

            case TableActionTypes.onChangePage: {
              return {
                ...tableState,
                page: payload
              };
            }
            case TableActionTypes.onChangeRowsPerPage: {
              return {
                ...tableState,
                page: 0,
                rowsPerPage: payload
              };
            }
            case TableActionTypes.reset: {
              return {
                ...TABLE_INIT_STATE
              };
            }
            case TableActionTypes.sorting: {
              return {
                ...tableState,
                sorting: {
                  id: name as string,
                  sortDirection: payload as TSortDirection
                }
              };
            }
            case TableActionTypes.onSearch: {
              return {
                ...tableState,
                searchInput: payload
              };
            }
            case TableActionTypes.search: {
              const searchInput = payload;
              if (!searchInput.trim())
                return {
                  ...tableState,
                  page: 0,
                  filters: [],
                  searchInput: searchInput.trim(),
                  filterData: tableState.rawData
                };

              const searchableColumns = tableState.columns.filter(
                column => column.searchable
              );

              if (searchableColumns.length === 0)
                throw new Error(
                  "No searchable column found, please set at least one searchable column to active search feature"
                );

              const filterData = tableState.rawData.filter(data => {
                return searchableColumns.some(column => {
                  const cell = parseCell(data[column.id], column);
                  return (
                    !!cell &&
                    cell
                      .toLocaleLowerCase()
                      .includes(searchInput.toLocaleLowerCase())
                  );
                });
              });

              return {
                ...tableState,
                page: 0,
                filters: [],
                searchInput,
                filterData
              };
            }

            case TableActionTypes.onFilter: {
              if (!name)
                throw new Error(
                  `action ${TableActionTypes.onFilter} is missing name`
                );
              if (tableState.columns.every(c => c.id !== name)) {
                throw new Error(`invalid filter id ${name}`);
              }

              const filters = tableState.filters.filter(
                filter => filter.id !== name
              );

              return {
                ...tableState,
                filters:
                  !payload.value || isEmpty(payload.value)
                    ? filters // remove previous filter
                    : [
                        ...filters,
                        {
                          id: name,
                          value: payload.value,
                          compare: payload.compare
                        } as TFilter
                      ]
              };
            }
            case TableActionTypes.execFilter: {
              if (tableState.filters.length === 0)
                return {
                  ...tableState,
                  filterData: tableState.rawData
                };

              const filterData = tableState.rawData.filter(row => {
                return tableState.filters.every(filter => {
                  const column = tableState.columns.find(
                    c => c.id === filter.id
                  );

                  return filter.compare(
                    filter.value,
                    row[filter.id],
                    (column as IColumn).parse
                  );
                });
              });

              return {
                ...tableState,
                page: 0,
                searchInput: "",
                filterData
              };
            }
            case TableActionTypes.cleanFilter: {
              return {
                ...tableState,
                page: 0,
                searchInput: "",
                filters: [],
                filterData: tableState.rawData
              };
            }
            default:
              return tableState;
          }
        },
        {
          ...TABLE_INIT_STATE
        }
      ),
      shareReplay(1)
    );

    const resetTable = () => {
      dispatch({
        type: TableActionTypes.reset
      });
    };

    return {
      subscribe: observer => tableState$.subscribe(observer),
      subscribeAction: observer => action$.subscribe(observer),
      dispatch,
      resetTable
    };
  }, []);

  return <TableProvider value={tableContext}>{props.children}</TableProvider>;
};

export * from "./Table";
export * from "./Column";
export * from "./interfaces";
export * from "./TableDetailCard";
export * from "./SearchBar";
export * from "./FilterBar";
export * from "./CustomWidget";
export * from "./OnActionChanged";

export default RxTable;
